import { useState, useEffect, useRef, useCallback } from "react";
import api, { downloadFile } from "../../../BrandOnBoarding/api";

const POLL_INTERVAL = 15000; // 15 seconds
const MAX_POLL_ATTEMPTS = 150; // 15 minutes

/** Map backend status values to the frontend's status vocabulary. */
function normalizeStatus(status) {
  if (status === "running" || status === "queued") return "in-progress";
  if (status === "success") return "completed";
  return status; // "pending", "completed", "failed", "cancelled" pass through
}

/**
 * Fetches report job history from backend.
 */
async function fetchJobsFromAPI(userId, transactionId) {
  try {
    const response = await api.get("/llm-reporting/html-history", {
      params: { userId, transactionId },
    });
    if (response.status === 200 && Array.isArray(response.data)) {
      return response.data
        .filter((job) => job.status !== "cancelled")
        .map((job) => ({
          jobId: job.id,
          status: normalizeStatus(job.status),
          selections: job.selections || {},
          createdAt: job.start_date || new Date().toISOString(),
          transactionId: String(job.transaction_id || transactionId),
        }));
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * useHTMLReportHistory
 *
 * Manages the list of HTML report generation jobs.
 * Fetches history from backend API and polls in-progress jobs on mount.
 */
export default function useHTMLReportHistory({ userId, transactionId }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const mountedRef = useRef(true);

  // ── polling (single interval over history endpoint) ───────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      pollAttemptsRef.current = 0;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return; // already running

    pollTimerRef.current = setInterval(async () => {
      if (!mountedRef.current) { stopPolling(); return; }

      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling();
        // mark any still-running jobs as failed
        setJobs((prev) =>
          prev.map((j) =>
            j.status === "in-progress" || j.status === "pending"
              ? { ...j, status: "failed" }
              : j
          )
        );
        return;
      }

      const updated = await fetchJobsFromAPI(userId, transactionId);
      if (!updated || !mountedRef.current) return;

      setJobs(updated);

      // stop once no in-progress jobs remain
      const hasRunning = updated.some(
        (j) => j.status === "in-progress" || j.status === "pending"
      );
      if (!hasRunning) stopPolling();
    }, POLL_INTERVAL);
  }, [userId, transactionId, stopPolling]);

  // ── load history ─────────────────────────────────────────────────────────

  const loadHistory = useCallback(async () => {
    if (!userId || !transactionId) return;
    setLoading(true);

    const resolved = (await fetchJobsFromAPI(userId, transactionId)) ?? [];

    if (mountedRef.current) {
      setJobs(resolved);
      // kick off a single poll if any jobs are still running
      const hasRunning = resolved.some(
        (j) => j.status === "in-progress" || j.status === "pending"
      );
      if (hasRunning) startPolling();
    }
    setLoading(false);
  }, [userId, transactionId, startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    loadHistory();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [loadHistory, stopPolling]);

  // ── public API ────────────────────────────────────────────────────────────

  /**
   * Optimistically add a new in-progress job and ensure polling is running.
   */
  const addJob = useCallback(
    (jobId, selections) => {
      const newJob = {
        jobId,
        status: "in-progress",
        selections,
        createdAt: new Date().toISOString(),
        transactionId: String(transactionId),
      };
      setJobs((prev) => [newJob, ...prev]);
      startPolling(); // no-op if already running
    },
    [transactionId, startPolling]
  );

  /**
   * Check if a completed job exists for the same transactionId + selections.
   * Returns the job or null.
   */
  const findCompletedJob = useCallback(
    (selections) => {
      return (
        jobs.find(
          (j) =>
            j.status === "completed" &&
            String(j.transactionId) === String(transactionId) &&
            j.selections?.category === selections?.category &&
            JSON.stringify(j.selections?.llms?.slice().sort()) ===
              JSON.stringify(selections?.llms?.slice().sort()) &&
            JSON.stringify(j.selections?.markets?.slice().sort()) ===
              JSON.stringify(selections?.markets?.slice().sort()) &&
            JSON.stringify(j.selections?.persona?.slice().sort()) ===
              JSON.stringify(selections?.persona?.slice().sort())
        ) || null
      );
    },
    [jobs, transactionId]
  );

  /**
   * Download a completed report by jobId.
   */
  const downloadReport = useCallback(async (jobId, txId) => {
    const result = await downloadFile(
      `/llm-reporting/download/${jobId}`,
      `Brand_Audit_HTML_Report_${txId || transactionId}.html`
    );
    return result;
  }, [transactionId]);

  /**
   * View HTML report in a new browser tab.
   */
  const viewReport = useCallback(async (jobId) => {
    try {
      const response = await api.get(`/llm-reporting/download/${jobId}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        // popup blocked fallback: auto-revoke after delay
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        throw new Error("popup_blocked");
      }
      // revoke after tab has loaded
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      throw err;
    }
  }, []);

  /**
   * Cancel or soft-delete a job by jobId.
   * Uses the existing cancel endpoint regardless of status.
   */
  const cancelJob = useCallback(async (jobId) => {
    await api.get(`/llm-reporting/cancel/${jobId}`);
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  return {
    jobs,
    loading,
    addJob,
    findCompletedJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory: loadHistory,
  };
}
